import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Modal3 } from "../../DIsplay/Modal/Modal3";
import TextInput from "../../form/TextInput";
import Button1 from "../../form/Button1";
import denyRefundRequest from "../../../controllers/booking/postTicketing/refund/denyRefundRequest";
import declineRefundBySupplier from "../../../controllers/booking/postTicketing/refund/declineRefundBySupplier";
import {
  getRefundDeclineActionLabel,
  isRefundCloseRequestStage,
  shouldChooseRefundDeclineSource,
} from "../../../data/flight/order/getRefundAdminGuidance";
import StaffApiErrorModal, {
  getStaffApiErrorCopy,
} from "../StaffApiErrorModal";

const SOURCE = {
  airline: "airline",
  team: "team",
};

const DEFAULT_AIRLINE_REASON = "Airline declined the refund";

/**
 * Decline / close a refund.
 * Airline/supplier → POST declineBySupplier (reason required).
 * Team / Ops → PATCH deny (rejectReason).
 */
export default function RefundDeclineModal({ open, setOpen, data, onDone }) {
  const [source, setSource] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [supplierResponseCode, setSupplierResponseCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  const order = typeof data === "object" && data ? data : null;
  const closing = isRefundCloseRequestStage(order);
  const needsChoice = shouldChooseRefundDeclineSource(order);

  useEffect(() => {
    if (!open) {
      setSource("");
      setRejectReason("");
      setSupplierResponseCode("");
      setLoading(false);
      return;
    }
    setSource("");
    setRejectReason(order?.rejectReason || "");
    setSupplierResponseCode("");
  }, [open, order?._id]);

  function close() {
    if (loading) return;
    setOpen?.(false);
  }

  async function handleConfirm() {
    if (!order?._id) return;

    if (needsChoice) {
      if (!source) {
        enqueueSnackbar(
          "Choose whether the airline or your team is declining.",
          { variant: "warning" },
        );
        return;
      }

      if (source === SOURCE.airline) {
        const reason = (rejectReason.trim() || DEFAULT_AIRLINE_REASON).trim();
        if (!reason) {
          enqueueSnackbar("Say why the airline declined.", {
            variant: "warning",
          });
          return;
        }
        setLoading(true);
        const body = { reason };
        if (supplierResponseCode.trim()) {
          body.supplierResponseCode = supplierResponseCode.trim();
        }
        const res = await declineRefundBySupplier(order._id, body).finally(
          () => setLoading(false),
        );
        if (res.return) {
          enqueueSnackbar("Recorded as airline / supplier declined.", {
            variant: "success",
          });
          setOpen?.(false);
          onDone?.(res.data);
        } else {
          setApiError(getStaffApiErrorCopy(res));
        }
        return;
      }

      // Team → deny
      const reason = rejectReason.trim();
      if (!reason) {
        enqueueSnackbar("Say why your team is declining this refund.", {
          variant: "warning",
        });
        return;
      }
      setLoading(true);
      const res = await denyRefundRequest(order._id, {
        rejectReason: reason,
      }).finally(() => setLoading(false));
      if (res.return) {
        enqueueSnackbar("Refund request has been declined.", {
          variant: "success",
        });
        setOpen?.(false);
        onDone?.(res.data);
      } else {
        setApiError(getStaffApiErrorCopy(res));
      }
      return;
    }

    // Pre-choice / close path — Ops deny only
    const reason = rejectReason.trim();
    if (!reason) {
      enqueueSnackbar(
        closing
          ? "Say why you are closing this request."
          : "Say why you are declining this refund.",
        { variant: "warning" },
      );
      return;
    }
    setLoading(true);
    const res = await denyRefundRequest(order._id, {
      rejectReason: reason,
    }).finally(() => setLoading(false));
    if (res.return) {
      enqueueSnackbar(
        closing
          ? "This refund request has been closed."
          : "Refund request has been declined.",
        { variant: "success" },
      );
      setOpen?.(false);
      onDone?.(res.data);
    } else {
      setApiError(getStaffApiErrorCopy(res));
    }
  }

  if (!open) return null;

  const title = closing ? "Close this request" : "Decline refund request";

  return (
    <>
      <StaffApiErrorModal
        open={Boolean(apiError)}
        setOpen={(next) => {
          if (!next) setApiError(null);
        }}
        title={apiError?.title}
        text={apiError?.text}
      />
      <Modal3 open={open} setOpen={setOpen} title={title}>
        <div className="card p-4 flex flex-col gap-4 min-w-[300px] max-w-[440px]">
          {needsChoice ? (
            <>
              <p className="text-sm text-gray-600">
                Who is declining this refund? Airline / supplier and Ops use
                different endpoints and end statuses.
              </p>
              <div className="flex flex-col gap-2">
                <label
                  className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 cursor-pointer ${
                    source === SOURCE.airline
                      ? "border-primary1 bg-primary1/5"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="refund-decline-source"
                    className="mt-1"
                    checked={source === SOURCE.airline}
                    onChange={() => {
                      setSource(SOURCE.airline);
                      if (!rejectReason.trim()) {
                        setRejectReason(DEFAULT_AIRLINE_REASON);
                      }
                    }}
                    disabled={loading}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-gray-900">
                      Airline / supplier declined
                    </span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Ends as RefundDeclinedBySupplier. No wallet credit.
                    </span>
                  </span>
                </label>
                <label
                  className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 cursor-pointer ${
                    source === SOURCE.team
                      ? "border-primary1 bg-primary1/5"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="refund-decline-source"
                    className="mt-1"
                    checked={source === SOURCE.team}
                    onChange={() => {
                      setSource(SOURCE.team);
                      if (rejectReason === DEFAULT_AIRLINE_REASON) {
                        setRejectReason("");
                      }
                    }}
                    disabled={loading}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-gray-900">
                      Declined by team
                    </span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Ops decision — ends as RefundRequestDenied.
                    </span>
                  </span>
                </label>
              </div>
              {source === SOURCE.airline ? (
                <div className="flex flex-col gap-3">
                  <TextInput
                    label="Reason"
                    multiline
                    rows={3}
                    placeholder={DEFAULT_AIRLINE_REASON}
                    value={rejectReason}
                    onChange={(ev) => setRejectReason(ev.target.value)}
                    disabled={loading}
                  />
                  <TextInput
                    label="Supplier response code (optional)"
                    placeholder="e.g. airline / GDS code"
                    value={supplierResponseCode}
                    onChange={(ev) => setSupplierResponseCode(ev.target.value)}
                    disabled={loading}
                  />
                </div>
              ) : null}
              {source === SOURCE.team ? (
                <TextInput
                  label=""
                  multiline
                  rows={4}
                  placeholder="e.g. Duplicate request, passenger no longer travelling, Ops decision"
                  value={rejectReason}
                  onChange={(ev) => setRejectReason(ev.target.value)}
                  disabled={loading}
                />
              ) : null}
            </>
          ) : (
            <>
              <h5 className="text-sm font-medium text-gray-900">
                {closing
                  ? "Why are you closing this request?"
                  : "Why are you declining this request?"}
              </h5>
              <TextInput
                label=""
                multiline
                rows={5}
                placeholder="e.g. Ticket already voided, wrong passenger, duplicate request"
                value={rejectReason}
                onChange={(ev) => setRejectReason(ev.target.value)}
                disabled={loading}
              />
            </>
          )}

          <div className="flex justify-end items-center gap-3">
            <Button1
              variant="outlined"
              className="btn-outlined flex-1"
              onClick={close}
              disabled={loading}
            >
              Cancel
            </Button1>
            <div>
              <Button1 onClick={handleConfirm} loading={loading}>
                {source === SOURCE.airline
                  ? "Record airline decline"
                  : getRefundDeclineActionLabel(order)}
              </Button1>
            </div>
          </div>
        </div>
      </Modal3>
    </>
  );
}
